// A list that shows its first few rows and hides the rest behind one toggle. Used by People,
// Customer journeys, Opportunities, orgs, meeting notes and the timeline alike, so the six read
// the same way and there is one place to fix rather than six near-copies.
//
// Two rules the callers depend on:
//
// * **The caller's order is never touched.** Contacts arrive ordered by recent activity and
//   journeys newest first; this shows a prefix of whatever it is handed. Sorting here would
//   silently undo an ordering that two layers below took care to produce.
// * **Expansion is per account.** The provider is keyed on the account id, so opening a panel
//   on a different company starts collapsed -- a block left expanded would otherwise reopen
//   against a list that belongs to somebody else.

import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

const DEFAULT_VISIBLE = 5;

interface CollapseValue {
  isExpanded: (key: string) => boolean;
  toggle: (key: string) => void;
}

const CollapseCtx = createContext<CollapseValue | null>(null);

export function CollapseProvider({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const value: CollapseValue = {
    isExpanded: key => expanded.has(key),
    toggle: key =>
      setExpanded(current => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
  };

  return <CollapseCtx.Provider value={value}>{children}</CollapseCtx.Provider>;
}

export function useCollapse(): CollapseValue {
  const value = useContext(CollapseCtx);
  if (!value) throw new Error('useCollapse used outside CollapseProvider');
  return value;
}

export function CollapseToggle({
  collapseKey,
  label,
}: {
  collapseKey: string;
  label: string;
}) {
  const { isExpanded, toggle } = useCollapse();
  return (
    <button
      type="button"
      className="morebtn"
      aria-expanded={isExpanded(collapseKey)}
      onClick={() => toggle(collapseKey)}
    >
      {label}
    </button>
  );
}

// Render a list, truncated unless expanded. `renderRow` is applied only to the items actually
// drawn -- items beyond the cut are never rendered rather than rendered and hidden with CSS,
// so a 31-contact account builds 5 rows of markup instead of 31.
export function CollapsibleList<Item>({
  collapseKey,
  items,
  renderRow,
  plural = 'rows',
  visible = DEFAULT_VISIBLE,
  inTable = false,
  columns = 1,
}: {
  collapseKey: string;
  items: Item[];
  renderRow: (item: Item, index: number) => ReactNode;
  // Both forms are passed rather than an "s" appended, because the block that most needs this
  // is People and "Show all 31 persons" is not English.
  plural?: string;
  visible?: number;
  // Whether the rows being rendered are `<tr>`s. The toggle has to be a table row when they
  // are: a `<div>` between `<tbody>` and `<tr>` is invalid HTML, and React says so loudly in
  // the console. The orgs table is the one caller that needs this, and it went unnoticed
  // because nobody expands a six-row table often enough to look.
  inTable?: boolean;
  columns?: number;
}) {
  const { isExpanded } = useCollapse();
  const isOpen = isExpanded(collapseKey);

  if (items.length <= visible) return <>{items.map(renderRow)}</>;

  const shown = isOpen ? items : items.slice(0, visible);
  const hiddenCount = items.length - visible;

  // The label names the count in both directions. "Show all 31 people" tells somebody what
  // they are about to get; a bare "Show more" makes them click to find out.
  const toggle = (
    <>
      <CollapseToggle
        collapseKey={collapseKey}
        label={isOpen ? `Show fewer ${plural}` : `Show all ${items.length} ${plural}`}
      />
      {isOpen ? null : <span className="morecount">{hiddenCount} more hidden</span>}
    </>
  );

  return (
    <>
      {shown.map(renderRow)}
      {inTable ? (
        <tr>
          <td className="morerow" colSpan={columns}>{toggle}</td>
        </tr>
      ) : (
        <div className="morerow">{toggle}</div>
      )}
    </>
  );
}

// The same toggle, for one long piece of prose rather than a list.
//
// Salesforce's Description is populated on 5 of the 314 accounts and where it exists it is
// unedited vendor marketing copy -- Zudu AI's runs to a full screen and ends in hashtags. Real
// data and worth keeping, but not worth handing the top of the panel to.
export function CollapsibleProse({
  collapseKey,
  children,
}: {
  collapseKey: string;
  children: ReactNode;
}) {
  const { isExpanded } = useCollapse();
  const isOpen = isExpanded(collapseKey);
  return (
    <>
      {/* The clamp is CSS rather than a substring: cutting the string would break mid-word and
          would have to guess how many characters make four lines at this width. */}
      <div className={isOpen ? '' : 'clamped'}>{children}</div>
      <div className="morerow">
        <CollapseToggle collapseKey={collapseKey} label={isOpen ? 'Show less' : 'Show all'} />
      </div>
    </>
  );
}
